import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { BASE_API_URL } from "../../config/config";
import { Logger } from '../../utils/logger';
import { trackEvent } from "../../config/firebase";
import "../login/onboarding.css";
import "./Signup.css";

// ─── Constants ───────────────────────────────────────────

const BusinessCategoryOptions = [
  {name: 'Real Estate', value: 'real_estate'},
  {name: 'Insurance Agents', value: 'insurance_agents'},
  {name: 'Financial Services', value: 'financial_services'},
  {name: 'Digital Marketing Agency', value: 'digital_marketing_agency'},
  {name: 'Event Management', value: 'event_management'},
  {name: 'Creator/Influencer/Online Coach', value: 'creator_influencer_online_coach'},
  {name: 'Construction and Interior Design', value: 'construction_interior_design'},
  {name: 'Health and Wellness', value: 'health_wellness'},
  {name: 'Education and Training Institutes', value: 'education_training_institutes'},
  {name: 'IT Services and Software Companies', value: 'it_services_software_companies'},
  {name: 'Manufacturing & B2B Sales', value: 'manufacturing_b2b_sales'},
  {name: 'Hospitality and Tourism', value: 'hospitality_tourism'},
  {name: 'Automotive Dealerships', value: 'automotive_dealerships'},
  {name: 'Telecalling Team', value: 'telecalling_team'},
  {name: 'Services Business', value: 'services_business'},
  {name: 'E-commerce', value: 'e_commerce'},
  {name: 'Travel Consultancy', value: 'travel_consultancy'},
  {name: 'Others', value: 'others'}
];

const TeamSizeOptions = [
  { label: 'Just me', value: '1', description: 'Solo' },
  { label: '2-5', value: '5', description: 'Small' },
  { label: '6-10', value: '10', description: 'Growing' },
  { label: '10-20', value: '20', description: 'Medium' },
  { label: '20+', value: '50', description: 'Large' }
];

const showcaseFeatures = [
  {
    title: 'WhatsApp Business API',
    description: 'Send messages, templates & broadcasts directly from your CRM'
  },
  {
    title: 'Smart Lead Management',
    description: 'Capture leads from multiple sources and track every interaction'
  },
  {
    title: 'Automated Follow-ups',
    description: 'Set up workflows that nurture leads on autopilot'
  },
  {
    title: 'Real-time Analytics',
    description: 'Monitor team performance and conversions at a glance'
  }
];

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3.5 8L6.5 11L12.5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── UTM / Install Referrer Capture ──────────────────────

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'ref'];

const captureInstallReferrer = (): Record<string, string> => {
  // Read any previously stored UTMs (e.g. from login page)
  let stored: Record<string, string> = {};
  try {
    stored = JSON.parse(localStorage.getItem('utm_params') || '{}');
  } catch { /* ignore malformed */ }

  // Capture from current URL search params
  const params = new URLSearchParams(window.location.search);
  UTM_KEYS.forEach(key => {
    const val = params.get(key);
    if (val) stored[key] = val;
  });

  // Capture document referrer (first visit only)
  if (document.referrer && !stored.referrer) {
    stored.referrer = document.referrer;
  }

  // Capture landing URL (first visit only)
  if (!stored.landing_page) {
    stored.landing_page = window.location.href;
  }

  // Persist for downstream use (e.g. signIn API)
  if (Object.keys(stored).length > 0) {
    localStorage.setItem('utm_params', JSON.stringify(stored));
  }

  return stored;
};

// ─── Component ───────────────────────────────────────────

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const stepRef = useRef(step);
  stepRef.current = step;

  // Determine login method
  const loginViaPhone = !!localStorage.getItem("phone");
  const loginViaSocial = !loginViaPhone && !!localStorage.getItem("email");

  const [formData, setFormData] = useState({
    firstName: localStorage.getItem("userName") || "",
    email: localStorage.getItem("email") || "",
    phone: localStorage.getItem("phone") || "",
    countryCode: localStorage.getItem("countryCode") || "91",
    companyName: "",
    teamSize: "",
    businessCategory: ""
  });

  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Capture UTM params on mount
  const installReferrer = useRef<Record<string, string>>({});

  useEffect(() => {
    installReferrer.current = captureInstallReferrer();
    Logger.log('[Signup] Install referrer captured:', installReferrer.current);

    // ── Analytics: onboarding started ──
    trackEvent('onboarding_web_started', {
      platform: 'web',
      login_type: loginViaPhone ? 'phone' : loginViaSocial ? 'social' : 'unknown',
      has_utm: Object.keys(installReferrer.current).length > 0,
      utm_source: installReferrer.current.utm_source || null,
      utm_medium: installReferrer.current.utm_medium || null,
      utm_campaign: installReferrer.current.utm_campaign || null,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Prevent browser back navigation ──
  // Back on step 2 goes to step 1; back on step 1 stays put.
  // This ensures users can't leave before hitting /user/onboard.
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (stepRef.current === 2) {
        setStep(1);
        trackEvent('onboarding_web_back_to_step1', { platform: 'web' });
      }
      // Re-push state to keep trapping
      window.history.pushState(null, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // ── Warn on tab close / refresh ──
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // ── Validation helpers ──

  const validatePhoneNumber = (number: string): boolean => {
    const phoneNumberPattern = /^[1-9]\d{9}$/;
    return phoneNumberPattern.test(number);
  };

  const handlePhoneChange = (value: string, country: any) => {
    const newCountryCode = country.dialCode;
    const numberWithoutCode = value.slice(newCountryCode.length);

    setFormData(prev => ({
      ...prev,
      phone: numberWithoutCode,
      countryCode: newCountryCode
    }));
    setIsPhoneValid(validatePhoneNumber(numberWithoutCode));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Step 1 → Step 2 ──

  const handleFirstStep = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (loginViaPhone) {
      if (!formData.email.trim()) {
        toast.error("Email is required");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("Please enter a valid email");
        return;
      }
    }

    if (loginViaSocial) {
      if (!formData.phone.trim()) {
        toast.error("Phone number is required");
        return;
      }
      if (!validatePhoneNumber(formData.phone)) {
        toast.error("Please enter a valid phone number");
        setIsPhoneValid(false);
        return;
      }
    }

    // ── Analytics: step 1 complete ──
    trackEvent('onboarding_web_step1_complete', {
      platform: 'web',
      login_type: loginViaPhone ? 'phone' : 'social',
      has_name: !!formData.firstName.trim(),
      has_email: !!formData.email.trim(),
      has_phone: !!formData.phone.trim(),
    });

    setStep(2);
  };

  // ── Submit (Step 2) ──

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.email.trim() || !formData.phone.trim() ||
        !formData.companyName.trim() || !formData.businessCategory || !formData.teamSize) {
      toast.error("Please fill in all required fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email");
      return;
    }

    if (!validatePhoneNumber(formData.phone)) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("auth_token");

      if (!token) {
        Logger.log('[Signup] Token missing - localStorage dump:', JSON.stringify(localStorage));
        throw new Error("Authentication token not found");
      }

      const response = await fetch(`${BASE_API_URL}/user/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`
        },
        body: JSON.stringify({
          profile: {
            firstName: formData.firstName.trim(),
            email: formData.email.trim(),
            phone: formData.phone,
            countryCode: formData.countryCode
          },
          organization: {
            name: formData.companyName.trim(),
            teamSize: formData.teamSize,
            businessCategory: formData.businessCategory
          }
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update profile");

      // ── Analytics: onboarding complete ──
      trackEvent('onboarding_web_complete', {
        platform: 'web',
        login_type: loginViaPhone ? 'phone' : 'social',
        team_size: formData.teamSize,
        business_category: formData.businessCategory,
        utm_source: installReferrer.current.utm_source || null,
        utm_medium: installReferrer.current.utm_medium || null,
        utm_campaign: installReferrer.current.utm_campaign || null,
      });

      toast.success("You're all set! Welcome to 3Sigma.");

      // Update localStorage
      localStorage.setItem("phone", formData.phone);
      localStorage.setItem("countryCode", formData.countryCode);
      localStorage.setItem("email", formData.email);
      localStorage.setItem("show_trial_welcome_modal", "true");

      navigate("/leads", { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error while updating profile");
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ────────────────────────────────────────────

  return (
    <div className="signup-page">
      {/* Left Panel — Feature Showcase */}
      <div className="signup-showcase">
        <div className="signup-showcase-content">
          <div className="signup-showcase-logo">
            <img src="assets/img/3sigma_logo.png" alt="3Sigma CRM" />
          </div>
          <h1 className="signup-showcase-title">
            Everything you need to close more deals
          </h1>
          <p className="signup-showcase-subtitle">
            Join thousands of businesses using 3Sigma to manage leads, automate follow-ups, and grow revenue.
          </p>
          <div className="signup-features">
            {showcaseFeatures.map((feature) => (
              <div className="signup-feature-item" key={feature.title}>
                <div className="signup-feature-icon">
                  <CheckIcon />
                </div>
                <div>
                  <div className="signup-feature-title">{feature.title}</div>
                  <div className="signup-feature-desc">{feature.description}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="signup-social-proof">
            Trusted by 10,000+ businesses across India
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="signup-form-side">
        <div className="onboarding-card signup-card">
          {/* Logo only shown on mobile */}
          <div className="onboarding-logo signup-mobile-logo">
            <img src="assets/img/3sigma_logo.png" alt="3Sigma CRM" />
          </div>

          {step === 1 ? (
            <>
              <div className="onboarding-header">
                <h2>Welcome to 3Sigma!</h2>
                <p className="onboarding-subtitle">
                  Great to have you here. Let's set up your account — it only takes a minute.
                </p>
              </div>

              <div className="onboarding-steps">
                <div className="onboarding-step active">1</div>
                <div className="onboarding-step-line"></div>
                <div className="onboarding-step">2</div>
              </div>

              <form onSubmit={handleFirstStep} className="onboarding-form">
                <div className="onboarding-form-group">
                  <label>Your Name</label>
                  <input
                    type="text"
                    className="onboarding-input"
                    name="firstName"
                    placeholder="Enter your full name"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>

                {loginViaPhone && (
                  <div className="onboarding-form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      className="onboarding-input"
                      name="email"
                      placeholder="Enter your work email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                )}

                {loginViaSocial && (
                  <div className="onboarding-form-group">
                    <label>Phone Number</label>
                    <div className="onboarding-phone-container">
                      <PhoneInput
                        country="in"
                        value={`${formData.countryCode}${formData.phone}`}
                        onChange={handlePhoneChange}
                        inputClass={!isPhoneValid ? "is-invalid" : ""}
                        containerClass="react-tel-input"
                      />
                      {!isPhoneValid && (
                        <span className="onboarding-error-text">
                          Please enter a valid mobile number
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <button type="submit" className="onboarding-btn-primary">
                  Continue
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="onboarding-header">
                <h2>About Your Business</h2>
                <p className="onboarding-subtitle">Help us personalize your experience</p>
              </div>

              <div className="onboarding-steps">
                <div className="onboarding-step completed">1</div>
                <div className="onboarding-step-line completed"></div>
                <div className="onboarding-step active">2</div>
              </div>

              <form onSubmit={handleSubmit} className="onboarding-form">
                <div className="signup-form-row">
                  <div className="onboarding-form-group">
                    <label>Organisation Name</label>
                    <input
                      type="text"
                      className="onboarding-input"
                      name="companyName"
                      placeholder="Your company name"
                      value={formData.companyName}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="onboarding-form-group">
                    <label>Business Category</label>
                    <div className="signup-dropdown" ref={categoryRef}>
                      <div
                        className={`signup-dropdown-trigger ${categoryOpen ? 'open' : ''} ${formData.businessCategory ? 'has-value' : ''}`}
                        onClick={() => setCategoryOpen(prev => !prev)}
                      >
                        <span className={formData.businessCategory ? '' : 'signup-dropdown-placeholder'}>
                          {formData.businessCategory
                            ? BusinessCategoryOptions.find(o => o.value === formData.businessCategory)?.name
                            : 'Select category'}
                        </span>
                        <svg className="signup-dropdown-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      {categoryOpen && (
                        <div className="signup-dropdown-menu">
                          {BusinessCategoryOptions.map(option => (
                            <div
                              key={option.value}
                              className={`signup-dropdown-item ${formData.businessCategory === option.value ? 'selected' : ''}`}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, businessCategory: option.value }));
                                setCategoryOpen(false);
                              }}
                            >
                              {option.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="onboarding-form-group">
                  <label>Team Size</label>
                  <div className="signup-team-grid">
                    {TeamSizeOptions.map(option => (
                      <div
                        key={option.value}
                        className={`signup-team-option ${formData.teamSize === option.value ? 'selected' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, teamSize: option.value }))}
                      >
                        <div className="signup-team-label">{option.label}</div>
                        <div className="signup-team-desc">{option.description}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="onboarding-button-group">
                  <button
                    type="button"
                    className="onboarding-btn-secondary"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="onboarding-btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="onboarding-loading">
                        <span className="onboarding-spinner"></span>
                        Please wait...
                      </span>
                    ) : (
                      "Let's Go"
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;
